import ChatOutlined from "@mui/icons-material/ChatOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import LanguageOutlined from "@mui/icons-material/LanguageOutlined";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SmsOutlined from "@mui/icons-material/SmsOutlined";
import WidgetsOutlined from "@mui/icons-material/WidgetsOutlined";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import React, { useEffect, useRef, useState } from "react";
import {
  AGENT_CHANNEL_EMAIL,
  AGENT_CHANNEL_OUTBOUND_SMS,
  AGENT_CHANNEL_OUTBOUND_VOICE,
  AGENT_CHANNEL_SMS,
  AGENT_CHANNEL_VOICE,
  AGENT_CHANNEL_WEBPAGE,
  AGENT_CHANNEL_WEBSITE,
} from "../../constants";
import { formatTimestamp, hostname, parseError } from "../../utils";
import ConversationDetailPanel from "./ConversationDetailPanel";

export interface ConversationData {
  agentId: string;
  agentName: string;
  agentAvatarImage?: string;
  sessionId: string;
  messageCount: number;
  lastMessageTime: number;
  firstUserMessage: string;
  firstUserMessageTime: number;
  latestAgentResponse?: string;
  latestAgentResponseTime?: number;
  latestResponseSuccessful: boolean;
  contactInfo?: any;
  channel?: string;
}

interface ConversationListResponse {
  conversations: ConversationData[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface ConversationsProps {
  currentUser: User;
  companyId: string;
  getToken: Function;
  eventListener: Function;
  currentPlan: string;
  employees: any[];
  isVisible?: boolean;
}

const Conversations: React.FC<ConversationsProps> = ({
  currentUser,
  companyId,
  getToken,
  eventListener,
  currentPlan,
  employees,
  isVisible = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [conversations, setConversations] = useState<ConversationData[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 20;

  // Filters
  const [agentFilter, setAgentFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [userAgents, setUserAgents] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  // Selected conversation
  const [selectedConversation, setSelectedConversation] =
    useState<ConversationData | null>(null);

  // Infinite scroll ref
  const listRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);

  const fetchConversations = async (pageNum: number = 1, append: boolean = false) => {
    if (!currentUser) {
      return;
    }

    if (pageNum === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const token = await getToken();
      const params = new URLSearchParams({
        page: pageNum.toString(),
        pageSize: pageSize.toString(),
        companyId: companyId,
      });

      if (agentFilter) {
        params.append("agentId", agentFilter);
      }

      if (dateFilter) {
        const days = parseInt(dateFilter);
        const startDate = Date.now() / 1000 - days * 24 * 60 * 60;
        params.append("startDate", startDate.toString());
      }

      if (channelFilter) {
        params.append("channels", channelFilter);
      } else {
        // By default, show inbound channels only
        params.append("channels", "website");
        params.append("channels", "webpage");
        params.append("channels", "email");
        params.append("channels", "sms");
        params.append("channels", "voice");
      }

      if (debouncedSearchQuery) {
        params.append("search", debouncedSearchQuery);
      }

      const url = `${hostname}/conversations?${params}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data: ConversationListResponse = response.data;

      if (append) {
        setConversations(prev => [...prev, ...data.conversations]);
      } else {
        setConversations(data.conversations);
        // Auto-select first conversation on desktop if none selected
        if (!isMobile && data.conversations.length > 0 && !selectedConversation) {
          setSelectedConversation(data.conversations[0]);
        }
      }

      setHasMore(data.conversations.length === pageSize && pageNum < data.totalPages);
      setPage(pageNum);
    } catch (error: any) {
      console.error("Error fetching conversations:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchUserAgents = async () => {
    if (!currentUser) return;

    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/agents?companyId=${companyId}&pageSize=100`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setUserAgents(response.data.items || []);
    } catch (error) {
      console.error("Error fetching user agents:", error);
    }
  };

  useEffect(() => {
    if (currentUser.uid && isVisible) {
      fetchUserAgents();
    }
  }, [currentUser, companyId, isVisible]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchQuery]);

  // Reset and fetch when filters change
  useEffect(() => {
    if (currentUser.uid && isVisible) {
      setPage(1);
      setSelectedConversation(null);
      fetchConversations(1, false);
    }
  }, [
    currentUser,
    agentFilter,
    dateFilter,
    channelFilter,
    debouncedSearchQuery,
    isVisible,
  ]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchConversations(page + 1, true);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, page]);

  const handleRefresh = () => {
    setPage(1);
    fetchConversations(1, false);
  };

  const handleSelectConversation = (conversation: ConversationData) => {
    setSelectedConversation(conversation);
  };

  const handleCloseDetail = () => {
    setSelectedConversation(null);
  };

  // Delete conversation
  const [deleteTarget, setDeleteTarget] = useState<ConversationData | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteConversation = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/agents/${deleteTarget.agentId}/conversations/${deleteTarget.sessionId}?companyId=${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Remove from list
      setConversations((prev) =>
        prev.filter(
          (c) =>
            !(c.agentId === deleteTarget.agentId && c.sessionId === deleteTarget.sessionId)
        )
      );
      // Clear selection if deleted conversation was selected
      if (
        selectedConversation?.agentId === deleteTarget.agentId &&
        selectedConversation?.sessionId === deleteTarget.sessionId
      ) {
        setSelectedConversation(null);
      }
    } catch (error: any) {
      console.error("Error deleting conversation:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const getContactDisplayName = (conversation: ConversationData) => {
    const contactInfo = conversation.contactInfo;
    if (!contactInfo) return "Anonymous";

    const channel = conversation.channel || "website";

    switch (channel) {
      case "voice":
      case "sms":
        return contactInfo.phone || contactInfo.phoneNumber ||
               [contactInfo.firstName, contactInfo.lastName].filter(Boolean).join(" ") ||
               "Anonymous";
      case "email":
        return contactInfo.name ||
               [contactInfo.firstName, contactInfo.lastName].filter(Boolean).join(" ") ||
               contactInfo.email?.split("@")[0] ||
               "Anonymous";
      case "widget":
      case "webpage":
      case "website":
      default:
        return [contactInfo.firstName, contactInfo.lastName].filter(Boolean).join(" ") ||
               contactInfo.email?.split("@")[0] ||
               "Anonymous";
    }
  };

  // Check if a string looks like a phone number
  const isPhoneNumber = (name: string) => {
    return /^[+\d\s\-()]+$/.test(name) && name.length >= 7;
  };

  const getContactInitials = (conversation: ConversationData) => {
    const name = getContactDisplayName(conversation);
    if (name === "Anonymous") return "?";
    if (isPhoneNumber(name)) return null; // Will show phone icon instead

    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case "email":
        return <EmailOutlined sx={{ fontSize: 16 }} />;
      case "sms":
      case "outbound_sms":
        return <SmsOutlined sx={{ fontSize: 16 }} />;
      case "voice":
      case "outbound_voice":
        return <PhoneOutlined sx={{ fontSize: 16 }} />;
      case "widget":
      case "website":
        return <WidgetsOutlined sx={{ fontSize: 16 }} />;
      case "webpage":
        return <LanguageOutlined sx={{ fontSize: 16 }} />;
      default:
        return <ChatOutlined sx={{ fontSize: 16 }} />;
    }
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  // Conversation list panel
  const renderConversationList = () => (
    <Box
      sx={{
        width: isMobile ? "100%" : 360,
        minWidth: isMobile ? "100%" : 360,
        borderRight: isMobile ? 0 : 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Conversations</Typography>
          <IconButton onClick={handleRefresh} disabled={loading} size="small">
            <RefreshOutlined />
          </IconButton>
        </Box>

        {/* Search */}
        <TextField
          size="small"
          fullWidth
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Filters */}
        <Box display="flex" gap={1} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 100, flex: 1 }}>
            <InputLabel>Agent</InputLabel>
            <Select
              value={agentFilter}
              label="Agent"
              onChange={(e) => setAgentFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {userAgents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 100, flex: 1 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={dateFilter}
              label="Period"
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="1">24h</MenuItem>
              <MenuItem value="7">7d</MenuItem>
              <MenuItem value="30">30d</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 100, flex: 1 }}>
            <InputLabel>Channel</InputLabel>
            <Select
              value={channelFilter}
              label="Channel"
              onChange={(e) => setChannelFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="website">{AGENT_CHANNEL_WEBSITE}</MenuItem>
              <MenuItem value="webpage">{AGENT_CHANNEL_WEBPAGE}</MenuItem>
              <MenuItem value="email">{AGENT_CHANNEL_EMAIL}</MenuItem>
              <MenuItem value="sms">{AGENT_CHANNEL_SMS}</MenuItem>
              <MenuItem value="voice">{AGENT_CHANNEL_VOICE}</MenuItem>
              <MenuItem value="outbound_sms">
                {AGENT_CHANNEL_OUTBOUND_SMS}
              </MenuItem>
              <MenuItem value="outbound_voice">
                {AGENT_CHANNEL_OUTBOUND_VOICE}
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Loading indicator */}
      {loading && <LinearProgress />}

      {/* Conversation List */}
      <Box
        ref={listRef}
        sx={{
          flex: 1,
          overflowY: "auto",
        }}
      >
        <List disablePadding>
          {conversations.map((conversation) => (
            <ListItemButton
              key={`${conversation.agentId}-${conversation.sessionId}`}
              onClick={() => handleSelectConversation(conversation)}
              selected={
                selectedConversation?.sessionId === conversation.sessionId &&
                selectedConversation?.agentId === conversation.agentId
              }
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                py: 1.5,
                "&.Mui-selected": {
                  bgcolor: "action.selected",
                },
              }}
            >
              <ListItemAvatar>
                <Avatar
                  src={conversation.agentAvatarImage}
                  sx={{
                    bgcolor: "primary.main",
                    width: 44,
                    height: 44,
                  }}
                >
                  {getContactInitials(conversation) || <PhoneOutlined />}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" noWrap sx={{ maxWidth: 160 }}>
                      {getContactDisplayName(conversation)}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {formatTimestamp(conversation.lastMessageTime).fromNow}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(conversation);
                        }}
                        sx={{
                          opacity: 0,
                          transition: "opacity 0.2s",
                          ".MuiListItemButton-root:hover &": { opacity: 1 },
                          p: 0.25,
                        }}
                      >
                        <DeleteOutline sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      sx={{ maxWidth: 220 }}
                    >
                      {truncateText(conversation.firstUserMessage, 40)}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                      <Chip
                        icon={getChannelIcon(conversation.channel || "website")}
                        label={conversation.agentName}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 24,
                          fontSize: "0.8rem",
                          "& .MuiChip-icon": { fontSize: 16 },
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    </Box>
                  </Box>
                }
              />
            </ListItemButton>
          ))}

          {/* Infinite scroll trigger */}
          <Box ref={observerTarget} sx={{ height: 20 }} />

          {loadingMore && !loading && (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <LinearProgress />
            </Box>
          )}
        </List>

        {/* Empty state */}
        {conversations.length === 0 && !loading && (
          <Box py={4} textAlign="center">
            <ChatOutlined
              sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary">
              No conversations found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {agentFilter || dateFilter || channelFilter || searchQuery
                ? "Try adjusting your filters"
                : "Conversations will appear here when users chat with your agents"}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  // Detail panel (right side)
  const renderDetailPanel = () => {
    if (!selectedConversation) {
      return (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.default",
          }}
        >
          <ChatOutlined sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Select a conversation
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Choose a conversation from the list to view details
          </Typography>
        </Box>
      );
    }

    return (
      <ConversationDetailPanel
        conversation={selectedConversation}
        getToken={getToken}
        companyId={companyId}
        onClose={isMobile ? handleCloseDetail : undefined}
        getContactDisplayName={getContactDisplayName}
      />
    );
  };

  const deleteConfirmDialog = (
    <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
      <DialogTitle>Delete Conversation</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete this conversation? All messages will be permanently removed.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
          Cancel
        </Button>
        <Button onClick={handleDeleteConversation} color="error" disabled={deleting}>
          {deleting ? "Deleting..." : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Mobile view: show either list or detail
  if (isMobile) {
    return (
      <Box
        sx={{
          height: "calc(100vh - 260px)",
          minHeight: 300,
          maxHeight: "calc(100vh - 260px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {selectedConversation ? renderDetailPanel() : renderConversationList()}
        {deleteConfirmDialog}
      </Box>
    );
  }

  // Desktop view: split panel
  return (
    <Box
      sx={{
        height: "calc(100vh - 260px)",
        minHeight: 300,
        maxHeight: "calc(100vh - 260px)",
        display: "flex",
        overflow: "hidden",
      }}
    >
      {renderConversationList()}
      {renderDetailPanel()}
      {deleteConfirmDialog}
    </Box>
  );
};

export default Conversations;
